import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { isAxiosError } from 'axios';
import mixpanel from 'mixpanel-browser';

import { Button, Headline } from '@freenow/wave';
import { config } from '@mytaxi/config-store';
import Logger from '@mytaxi/logging';
import { ErrorPanel } from '@mytaxi/react-error-screens';

import { getAvailableBusinessAccounts } from '../../account/api/business-account-information.api';
import { AuthenticationContext } from '../../authentication/context/AuthenticationContext';
import { trackLoginError } from '../../tracking/helpers/googleTagManager/trackEvents';
import { authorizedAxiosInstance } from '../api/axios-instance';
import { CenteredContentWrapper, CenteredLoadingIndicator, ErrorScreen, GreyRelativeWrapper } from '../components';
import LOCAL_STORAGE_KEYS from '../constants/common.localStorageKeys';
import { BusinessAccount, getAvailableCentralOrganisationBusinessAccounts } from '../api/central-organisations.api';
import { httpStatusCodes } from '../constants/httpStatusCodes';
import { useFormatMessage } from '../hooks/useFormatMessage';
import { ExternalPaymentSolution } from '../types/ExternalPaymentSolution';
import { CentralOrganisationContext } from './CentralOrganisationContext';
import { ROUTES } from '../../routing/constants/routes';

interface BusinessAccountList {
    id: number;
    name: string;
}

export enum InvoicingScheduleType {
    PER_TRIP = 'PER_TRIP',
    END_OF_MONTH = 'END_OF_MONTH',
}

export enum OnlineBookingDefaultPaymentType {
    CASH = 'CASH',
    ACCOUNT = 'ACCOUNT',
}

export enum BenefitsType {
    MoBuInvoicing = 'MOBU_INVOICING',
    NoBenefits = 'NO_BENEFITS',
}

export interface Address {
    addressLine: string;
    city: string;
    cityCode: string;
    countryCode: string;
}

export enum Status {
    Ready = 'READY',
    Onboarding = 'ONBOARDING',
    Deleted = 'DELETED',
}

export enum OnboardingTourState {
    Pending = 'PENDING',
    DismissedOnce = 'DISMISSED_ONCE',
    DismissedMultiple = 'DISMISSED_MULTIPLE',
    DismissedPermanently = 'DISMISSED_PERMANENTLY',
    Completed = 'COMPLETED',
}

export interface MirroringDetails {
    sourceBusinessAccountId: number;
    sourceBusinessAccountName: string;
}

export interface BusinessAccountDetails {
    id: number;
    officeId: number;
    officeHeadquarterCurrency?: string;
    name: string;
    legalName?: string;
    taxId?: string;
    vatId?: string;
    allowReceiptForwarding: boolean;
    contactEmail?: string;
    contactPhone?: string;
    commercialRegisterNumber?: string;
    specialAccountNumber?: string;
    address?: Address;
    invoiceEmailAddress?: string;
    invoiceAddress?: Address;
    verified: boolean;
    countryCode: string;
    locale: string;
    contactPerson?: {
        firstName: string;
        lastName: string;
        gender?: string;
        emailAddress: string;
        phone: string;
    };
    testAccount: boolean;
    settings: {
        preventTip: boolean;
        costCenterRequired: boolean;
        projectNameRequired: boolean;
        multiCreditCardAllowed: boolean;
        viewHistoryForBookers: boolean;
        invoicingScheduleType: InvoicingScheduleType;
        accessHistoryForBookers: string;
        servicePlan: 'BASIC' | 'PREMIUM';
        invoicingFormat: string;
        tripHistoryReportLayoutId: string;
        notesMandatory: boolean;
        useInAppName: boolean;
        inAppName: string;
        onlineBookingDefaultPaymentType: OnlineBookingDefaultPaymentType;
        sendSmsToBookerEnabled: boolean;
        useBookerCostCenterForGuestBooking: boolean;
        forceBookerToEnterNameAndPhone: boolean;
        bookingEnabled: boolean;
        mirroringDetails?: MirroringDetails | null;
        domainDiscoveryEnabled: boolean;
    };
    pecOfficialEmailAddress?: string;
    codiceUnivocoId?: string;
    billingAllowed: boolean;
    companyType: string;
    businessAccountType?: 'COMPANY' | 'HOTEL' | 'DISPATCH' | 'RESTAURANT';
    employeeBenefitsBusinessAccount: boolean;
    benefitsType: BenefitsType;
    status: Status;
    onboardingDetails?: {
        companyInfo: boolean;
        paymentMethod: boolean;
        adminPanelTour?: OnboardingTourState;
    };
    vatIdNotAvailable?: boolean;
    financeEmailAddress?: string;
    centralOrganisationId?: number;
    legalEntityId?: number;
}

interface ExternalPaymentSolutionResponse {
    externalPaymentSolution: ExternalPaymentSolution;
}

interface MultiMobilityDetailsResponse {
    allowed: boolean;
}

export interface IBusinessAccountContext {
    externalPaymentSolution: ExternalPaymentSolution;
    multiMobilityDetails?: MultiMobilityDetailsResponse;
    currentBusinessAccount?: BusinessAccountDetails;
    setCurrentBusinessAccountId: (businessAccountId: number, pathAfterUpdate?: string) => void;
    updateCurrentBusinessAccount: (businessAccount: BusinessAccountDetails) => void;
    refreshCurrentBusinessAccount: () => void;
    businessAccounts?: BusinessAccountList[];
    coBusinessAccounts?: BusinessAccount[];
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    totalPages?: number;
    totalBusinessAccounts: number;
    searchedTotalBusinessAccounts: number;
    loadingCentralOrganisationBusinessAccounts: boolean;
    isSelectingBusinessAccount: boolean;
    setIsSelectingBusinessAccount: Dispatch<SetStateAction<boolean>>;
    refetchCoBusinessAccounts: () => void;
}

const getInitialBusinessAccountId = (): number | undefined => {
    const lastBusinessAccountIdStr = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_BUSINESS_ACCOUNT_ID);
    return lastBusinessAccountIdStr ? Number.parseInt(lastBusinessAccountIdStr) : undefined;
};

export const BusinessAccountContext = createContext<IBusinessAccountContext>({
    setCurrentBusinessAccountId: () => {
        throw Error('BusinessAccountContext is not initialized.');
    },
    updateCurrentBusinessAccount: () => {
        throw Error('BusinessAccountContext is not initialized.');
    },
    refreshCurrentBusinessAccount: () => {
        throw Error('BusinessAccountContext is not initialized.');
    },
    businessAccounts: [],
    coBusinessAccounts: [],
    externalPaymentSolution: ExternalPaymentSolution.NONE,
    page: 0,
    setPage: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    search: '',
    setSearch: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    totalPages: undefined,
    totalBusinessAccounts: 0,
    searchedTotalBusinessAccounts: 0,
    loadingCentralOrganisationBusinessAccounts: false,
    isSelectingBusinessAccount: false,
    setIsSelectingBusinessAccount: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    refetchCoBusinessAccounts: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
});

export const BusinessAccountContextProvider = (props: { children: React.ReactNode }) => {
    const formatMessage = useFormatMessage();
    const history = useHistory();
    const { logout } = useContext(AuthenticationContext);
    const {
        currentCentralOrganisation,
        centralOrganisations,
        updateCurrentCentralOrganisation,
        centralOrganisationChanged,
        setCentralOrganisationChanged,
    } = useContext(CentralOrganisationContext);
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalBusinessAccounts, setTotalBusinessAccounts] = useState<number>(0);
    const [searchedTotalBusinessAccounts, setSearchedTotalBusinessAccounts] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [currentBusinessAccount, setCurrentBusinessAccount] = useState<BusinessAccountDetails | undefined>();
    const [currentBusinessAccountId, setCurrentBusinessAccountId] = useState<number | undefined>(
        getInitialBusinessAccountId,
    );
    const [businessAccounts, setBusinessAccounts] = useState<BusinessAccountList[] | undefined>(undefined);
    const [coBusinessAccounts, setCoBusinessAccounts] = useState<BusinessAccount[] | undefined>(undefined);
    const [businessAccountListError, setBusinessAccountListError] = useState(false);
    const [businessAccountDetailsError, setBusinessAccountDetailsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSelectingBusinessAccount, setIsSelectingBusinessAccount] = useState(false);
    const [loadingCentralOrganisationBusinessAccounts, setLoadingCentralOrganisationBusinessAccounts] = useState(false);
    const [externalPaymentSolution, setExternalPaymentSolution] = useState<ExternalPaymentSolution>(
        ExternalPaymentSolution.NONE,
    );
    const [multiMobilityDetails, setMultiMobilityDetails] = useState<MultiMobilityDetailsResponse>();
    const [pushAfterUpdate, setPushAfterUpdate] = useState<string>();

    const updateCurrentBusinessAccountId = useCallback(
        (businessAccountId: number, pathAfterUpdate?: string) => {
            if (businessAccountId === currentBusinessAccountId && pathAfterUpdate) {
                history.push(pathAfterUpdate);
                setPushAfterUpdate(undefined);
            } else if (businessAccountId !== currentBusinessAccountId && pathAfterUpdate) {
                setPushAfterUpdate(pathAfterUpdate);
            }
            setCurrentBusinessAccountId(businessAccountId);
            if (businessAccountId) {
                localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_BUSINESS_ACCOUNT_ID, businessAccountId.toString());
            } else {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_BUSINESS_ACCOUNT_ID);
            }
        },
        [currentBusinessAccountId, history],
    );

    const fetchAvailableCentralOrganisationBusinessAccounts = useCallback(async () => {
        if (
            currentCentralOrganisation?.id &&
            config.getItem('featureFlags.newBaStructureEnabled') &&
            !loadingCentralOrganisationBusinessAccounts
        ) {
            try {
                setLoadingCentralOrganisationBusinessAccounts(true);
                const response = await getAvailableCentralOrganisationBusinessAccounts(
                    currentCentralOrganisation.id,
                    page,
                    searchQuery,
                );

                if (!searchQuery) {
                    if (response.data.pageMetadata.totalElements === 0 && centralOrganisations?.length) {
                        updateCurrentCentralOrganisation(centralOrganisations[0].id);
                        return;
                    }
                    setTotalBusinessAccounts(response.data.pageMetadata.totalElements);
                }

                setCoBusinessAccounts(response.data.businessAccounts);
                setSearchedTotalBusinessAccounts(response.data.pageMetadata.totalElements);
                setTotalPages(response.data.pageMetadata.totalPages);

                if (centralOrganisationChanged) {
                    const firstBAId = response.data.businessAccounts[0]?.id;

                    if (firstBAId) {
                        updateCurrentBusinessAccountId(firstBAId);
                    } else {
                        Logger.error('Central Organisation has no Business Accounts.');
                        localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_BUSINESS_ACCOUNT_ID);
                    }
                    setCentralOrganisationChanged(false);
                } else if (!currentBusinessAccountId) {
                    const firstBAId = response.data.businessAccounts[0]?.id;

                    if (firstBAId) {
                        updateCurrentBusinessAccountId(firstBAId);
                    } else {
                        Logger.error('Default CO has no BAs on initial load.');
                    }
                }
            } catch (error) {
                if (
                    isAxiosError(error) &&
                    error.response?.status === httpStatusCodes.CLIENT_ERROR.FORBIDDEN &&
                    centralOrganisations
                ) {
                    updateCurrentCentralOrganisation(centralOrganisations[0].id);
                } else {
                    Logger.error('Failed to fetch available business accounts in central organisation', { error });
                    setBusinessAccountListError(true);
                }
            } finally {
                setLoadingCentralOrganisationBusinessAccounts(false);
            }
        }
    }, [
        currentCentralOrganisation?.id,
        loadingCentralOrganisationBusinessAccounts,
        page,
        searchQuery,
        centralOrganisationChanged,
        currentBusinessAccountId,
        centralOrganisations,
        updateCurrentCentralOrganisation,
        setCentralOrganisationChanged,
        updateCurrentBusinessAccountId,
    ]);

    useEffect(() => {
        fetchAvailableCentralOrganisationBusinessAccounts();
        // only want to fetch CO BAs when CO, page or search query changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCentralOrganisation, page, searchQuery]);

    useEffect(() => {
        if (!config.getItem('featureFlags.newBaStructureEnabled')) {
            getAvailableBusinessAccounts()
                .then((response) => {
                    if (response && response.data) {
                        const businessAccountsResponse = response.data.businessAccountBasicList.map((it) => ({
                            id: it.companyId,
                            name: it.companyName,
                        }));

                        setBusinessAccounts(businessAccountsResponse);

                        const lastBusinessAccountId = localStorage.getItem(
                            LOCAL_STORAGE_KEYS.CURRENT_BUSINESS_ACCOUNT_ID,
                        );

                        const newBusinessAccount = lastBusinessAccountId
                            ? businessAccountsResponse.find((it) => it.id === Number.parseInt(lastBusinessAccountId))
                            : businessAccountsResponse[0];

                        if (newBusinessAccount) {
                            setCurrentBusinessAccountId(newBusinessAccount.id);
                        } else if (businessAccountsResponse.length > 0) {
                            setCurrentBusinessAccountId(businessAccountsResponse[0].id);
                        }
                    }
                })
                .catch((error) => {
                    Logger.error('Failed to fetch available business accounts', { error });
                    setBusinessAccountListError(true);
                });
        }
    }, []);

    const loadBusinessAccount = useCallback(
        (businessAccountId: number) => {
            setLoading(true);
            const businessAccountInformationRequest = authorizedAxiosInstance.get<BusinessAccountDetails>(
                `${config.getItem('apgsUrl')}/v2/business-accounts/${businessAccountId}`,
            );

            const externalPaymentSolutionRequest = authorizedAxiosInstance.get<ExternalPaymentSolutionResponse>(
                `${config.getItem('apgsUrl')}/v1/business-accounts/${businessAccountId}/external-payment-solution`,
            );

            const multiMobilityDetailsRequest = authorizedAxiosInstance.get<MultiMobilityDetailsResponse>(
                `${config.getItem('apgsUrl')}/v1/business-accounts/${businessAccountId}/multi-mobility-details`,
            );

            Promise.all([
                businessAccountInformationRequest,
                externalPaymentSolutionRequest,
                multiMobilityDetailsRequest,
            ])
                .then(
                    ([
                        businessAccountInformationResponse,
                        externalPaymentSolutionResponse,
                        multiMobilityDetailsResponse,
                    ]) => {
                        const loadedBusinessAccount = businessAccountInformationResponse.data;
                        const businessAccountCentralOrganisation = loadedBusinessAccount.centralOrganisationId;

                        if (
                            businessAccountCentralOrganisation &&
                            currentCentralOrganisation &&
                            businessAccountCentralOrganisation !== currentCentralOrganisation.id
                        ) {
                            Logger.warn(`Business Account/Central Organisation desync detected.`);
                            updateCurrentCentralOrganisation(businessAccountCentralOrganisation, false);
                        }
                        setExternalPaymentSolution(externalPaymentSolutionResponse.data.externalPaymentSolution);

                        setMultiMobilityDetails(multiMobilityDetailsResponse.data);

                        setCurrentBusinessAccount(businessAccountInformationResponse.data);
                        mixpanel.register({ BusinessAccountId: businessAccountInformationResponse.data.id });

                        if (pushAfterUpdate) {
                            history.push(pushAfterUpdate);
                            setPushAfterUpdate(undefined);
                        }
                    },
                )
                .catch((error) => {
                    if (
                        isAxiosError(error) &&
                        (error.response?.status === httpStatusCodes.CLIENT_ERROR.FORBIDDEN ||
                            error.response?.status === httpStatusCodes.CLIENT_ERROR.NOT_FOUND) &&
                        coBusinessAccounts?.length
                    ) {
                        Logger.warn(`Verification failed for BA ${businessAccountId}. Resetting to default.`, {
                            error,
                        });
                        updateCurrentBusinessAccountId(coBusinessAccounts[0].id);
                        history.push(ROUTES.ROOT);
                    } else {
                        Logger.error(`Failed to fetch business account details for BA: ${businessAccountId}`, {
                            error,
                        });
                        setBusinessAccountDetailsError(true);
                    }
                });
        },
        [
            currentCentralOrganisation,
            pushAfterUpdate,
            updateCurrentCentralOrganisation,
            history,
            updateCurrentBusinessAccountId,
            coBusinessAccounts,
        ],
    );

    useEffect(() => {
        if (currentBusinessAccountId) {
            Logger.setMetadata('businessAccountId', currentBusinessAccountId);
            loadBusinessAccount(currentBusinessAccountId);
        } else {
            Logger.removeMetadata('businessAccountId');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusinessAccountId]);

    if (loading) {
        if (
            (currentBusinessAccountId && currentBusinessAccount) ||
            businessAccounts?.length === 0 ||
            businessAccountDetailsError ||
            businessAccountListError
        ) {
            setLoading(false);
        }

        return <CenteredLoadingIndicator />;
    }

    if (businessAccountListError) {
        return (
            <ErrorPanel
                title={formatMessage('businessAccountContext.failedToLoadBusinessAccounts')}
                subTitle={formatMessage('errorPanel.header.headerSubTitle')}
                detailsTitle=""
                buttonTitle=""
                errorStack=""
                showMoreInformation={false}
            />
        );
    }

    if (businessAccountDetailsError) {
        return (
            <ErrorPanel
                title={formatMessage('businessAccountContext.failedToLoadBusinessAccountDetails')}
                subTitle={formatMessage('errorPanel.header.headerSubTitle')}
                detailsTitle=""
                buttonTitle=""
                errorStack=""
                showMoreInformation={false}
            />
        );
    }

    if (businessAccounts?.length === 0) {
        trackLoginError('Not an Admin');

        return (
            <GreyRelativeWrapper>
                <CenteredContentWrapper>
                    <ErrorScreen>
                        <Headline as="h4" mb={2}>
                            <FormattedMessage id="login.errors.noAdmin" />
                        </Headline>
                        <Button variant="secondary" size="small" onClick={() => logout()}>
                            <FormattedMessage id="businessAccountContext.logout" />
                        </Button>
                    </ErrorScreen>
                </CenteredContentWrapper>
            </GreyRelativeWrapper>
        );
    }

    if (!currentBusinessAccountId && businessAccounts) {
        Logger.debug(`Not an administrator for ${currentBusinessAccount?.id}: switching to ${businessAccounts[0].id}.`);

        setCurrentBusinessAccountId(businessAccounts[0].id);

        return null;
    }

    const updateCurrentBusinessAccount = (businessAccount: BusinessAccountDetails) => {
        setCurrentBusinessAccount(businessAccount);
    };

    const refreshCurrentBusinessAccount = () => {
        if (currentBusinessAccountId) {
            loadBusinessAccount(currentBusinessAccountId);
        }
    };

    const context: IBusinessAccountContext = {
        currentBusinessAccount,
        externalPaymentSolution,
        multiMobilityDetails,
        setCurrentBusinessAccountId: updateCurrentBusinessAccountId,
        updateCurrentBusinessAccount,
        refreshCurrentBusinessAccount,
        businessAccounts,
        coBusinessAccounts,
        page,
        setPage,
        search: searchQuery,
        setSearch: setSearchQuery,
        totalBusinessAccounts,
        totalPages,
        searchedTotalBusinessAccounts,
        loadingCentralOrganisationBusinessAccounts,
        isSelectingBusinessAccount,
        setIsSelectingBusinessAccount,
        refetchCoBusinessAccounts: fetchAvailableCentralOrganisationBusinessAccounts,
    };

    return <BusinessAccountContext.Provider {...props} value={context} />;
};
